/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { withRouter, useParams, useHistory } from "react-router-dom";
import Page from "components/common_components/Page/Page";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import POSContext from "./POSContext";
import { getDateMillis } from "utils/dateUtils";
import Header from "components/common_components/Header";
import { ConfirmDialog } from "components/common_components/Dialogs";
import Grid from "@material-ui/core/Grid";
import MasterForm from "./MasterForm";
import DetailForm from "./DetailForm";
import DetailTable from "./DetailTable";
import { hasElements } from "utils/arrays";
import Button from "components/common_components/Button";
import Report from "./Report";

function POSEntry(props) {
    const { mode } = props;
    const adding = mode === "add";
    const updating = mode === "update";
    const history = useHistory();
    const { id } = useParams();
    const [posEntry, setPosEntry] = useState(undefined);
    const [selectedDetail, setSelectedDetail] = useState(undefined);
    const [resources, setResources] = useState({
        categories: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const dispatch = useDispatch();

    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const { Master, POSCategories, isUserCreating, setIsUserCreating, documentIssuer } = useContext(POSContext);

    function getPosEntry() {
        setIsLoading(true);
        Master.getOneById({
            id,
            onSuccess: (data) => {
                setPosEntry(data);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    function getResources() {
        POSCategories.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({ ...prev, categories: data }));
            },
            onError: (error) => {
                setResources((prev) => ({ ...prev, categories: [] }));
                dispatch(notifyError(error));
            },
        });
    }

    useEffect(() => {
        if (id) {
            getPosEntry();
        }
    }, [id]);

    useEffect(() => {
        getResources();
    }, []);

    function onCancelHandler() {
        history.push(`/pos`);
    }

    function onSuccess(newMasterId, setSubmitting) {
        dispatch(notifySuccess(`POS Entry ${adding ? "created" : "updated"} successfully!`));
        setSubmitting(false);
        if (adding) {
            setIsUserCreating(true);
        }
        history.push(`/pos/${newMasterId}/update`);
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            onCancelHandler();
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { userId, date, notes } = values;

        const dateInMillis = getDateMillis(date);

        const apiCall = adding ? Master.add : Master.update;

        const execute = () => {
            setSubmitting(true);
            apiCall({
                payload: {
                    id: updating ? id : null,
                    userId,
                    date: dateInMillis,
                    notes: notes.trim(),
                },
                onSuccess: (newMasterId) => onSuccess(newMasterId, setSubmitting),
                onError: (error) => onError(error, setSubmitting, setErrors),
            });
        };

        execute();
    }

    const onDetailSuccessHandler = (newId, payload) => {
        const filteredCurrentValue = hasElements(posEntry.detailEntries) ? posEntry.detailEntries.filter((x) => x.id !== newId) : [];
        setPosEntry((prev) => ({ ...prev, detailEntries: [...filteredCurrentValue, { ...payload, id: newId }] }));
    };

    const onDetailsDeletedSuccessHandler = (deletedIds) => {
        if (hasElements(deletedIds)) {
            setPosEntry((prev) => ({ ...prev, detailEntries: (prev.detailEntries || []).filter((x) => !deletedIds.includes(x.id)) }));
        }
    };

    return (
        <Page>
            <Header title="POS" />
            {isLoading && <LoadingPage />}
            {!isLoading && (
                <Grid container direction="column" spacing={4} className="pos">
                    <Grid item>
                        <MasterForm masterData={posEntry} {...props} onCancel={onCancelHandler} onSubmit={onSubmitHandler} />
                    </Grid>
                    {updating && posEntry && posEntry.id && (
                        <Grid item>
                            <Grid container direction="row" justifyContent="flex-start" spacing={4}>
                                <Grid item sm={12} md={4}>
                                    <DetailForm
                                        posEntryMasterId={posEntry.id}
                                        detailData={selectedDetail}
                                        resources={resources}
                                        onCancel={() => setSelectedDetail(undefined)}
                                        onDetailSuccess={onDetailSuccessHandler}
                                        isUserCreating={isUserCreating}
                                        setSelectedDetail={setSelectedDetail}
                                    />
                                </Grid>
                                <Grid item sm={12} md={8}>
                                    <DetailTable
                                        data={posEntry.detailEntries}
                                        resources={resources}
                                        onRowClick={setSelectedDetail}
                                        onRowsDeleted={onDetailsDeletedSuccessHandler}
                                        isUserCreating={isUserCreating}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <div className="button-group">
                                        <Button outline onClick={onCancelHandler}>
                                            Back
                                        </Button>
                                        <Button outline secondary onClick={() => setShowReceipt(!showReceipt)}>
                                            {showReceipt ? "Close receipt" : "Open receipt"}
                                        </Button>
                                    </div>
                                </Grid>
                                {showReceipt && (
                                    <Grid item xs={12}>
                                        <Report reportProps={{ receipt: posEntry, resources, documentIssuer }} reportType="receipt" />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            )}
            <ConfirmDialog {...confirmDialogProps} />
        </Page>
    );
}

POSEntry.propTypes = {
    mode: PropTypes.oneOf(["add", "update"]).isRequired,
    title: PropTypes.string,
};

export default withRouter(POSEntry);
